import React, { useContext, useEffect, useState } from 'react';
import { Context } from '../../../../Context';
import operationData from '../../../../../Operation/data.json';
import { fetchUsers, createUser, updateUser, deleteUser } from '../../../../utils/foodDeliveryApi';
import FoodDeliveryTabs from './FoodDeliveryTabs';

const normalizeListResponse = (response) => {
  if (Array.isArray(response)) return response;
  const candidate = response?.data || response?.users || response?.result || response?.body;
  return Array.isArray(candidate) ? candidate : [];
};

const emptyForm = { name: '', email: '', phone: '' };

const StaffManagement = () => {
  const { institutionData } = useContext(Context);
  const institution = operationData.foodDeliveryInstitution;
  const activeInstitution = institution || institutionData?.institution || '';

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingUser, setEditingUser] = useState(null);
  const [error, setError] = useState('');

  const loadUsers = async () => {
    if (!activeInstitution) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetchUsers(activeInstitution);
      let normalizedUsers = normalizeListResponse(res);
      // Filter to only show delivery staff with actual data
      normalizedUsers = normalizedUsers.filter(u => 
        u && 
        (String(u.userType || u.type || '').toLowerCase() === 'delivery') &&
        (u.name || u.email || u.phone || u.userName || u.emailId || u.phoneNumber)
      );
      setUsers(normalizedUsers);
    } catch (err) {
      setError(err?.message || 'Failed to load staff.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadUsers(); }, [activeInstitution]);

  const handleCreate = async () => {
    setLoading(true);
    setError('');
    try {
      await createUser(activeInstitution, { name: form.name, email: form.email, phone: form.phone });
      setForm(emptyForm);
      await loadUsers();
    } catch (err) {
      setError(err?.message || 'Failed to create user.');
    } finally { setLoading(false); }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setForm({ 
      name: user.name || user.userName || user.fullName || user.firstName || '', 
      email: user.email || user.emailId || user.emailAddress || '', 
      phone: user.phone || user.phoneNumber || user.mobileNumber || '' 
    });
  };

  const handleUpdate = async () => {
    if (!editingUser) return;
    setLoading(true);
    setError('');
    try {
      const payload = { ...editingUser, name: form.name, email: form.email, phone: form.phone };
      await updateUser(activeInstitution, payload);
      setEditingUser(null);
      setForm(emptyForm);
      await loadUsers();
    } catch (err) {
      setError(err?.message || 'Failed to update user.');
    } finally { setLoading(false); }
  };

  const handleDelete = async (cognitoId) => {
    setLoading(true);
    setError('');
    try {
      await deleteUser(activeInstitution, cognitoId);
      await loadUsers();
    } catch (err) {
      setError(err?.message || 'Failed to delete user.');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen w-full">
      <FoodDeliveryTabs />
      <div className="px-4 py-6 lg:px-8">
        <div className="mx-auto max-w-4xl space-y-6">
        <div className="bg-white px-6 py-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-extrabold text-[#1F2937]">Food Delivery — Staff Management</h2>
              <p className="mt-1 text-sm text-gray-500">{activeInstitution}</p>
            </div>
          </div>

          {error && <div className="mt-3 text-sm text-red-600">{error}</div>}
          {loading && <div className="mt-3 text-sm text-gray-600">Loading...</div>}

          <div className="mt-4 grid grid-cols-1 gap-3">
            <input placeholder="Name" value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} className="rounded-lg border border-gray-300 px-3 py-2" />
            <input placeholder="Email" value={form.email} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} className="rounded-lg border border-gray-300 px-3 py-2" />
            <input placeholder="Phone" value={form.phone} onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))} className="rounded-lg border border-gray-300 px-3 py-2" />
            <div className="flex gap-2">
              {editingUser ? (
                <>
                  <button onClick={handleUpdate} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg">Save</button>
                  <button onClick={() => { setEditingUser(null); setForm(emptyForm); }} className="px-4 py-2 rounded-lg border">Cancel</button>
                </>
              ) : (
                <>
                  <button onClick={handleCreate} className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg">Create</button>
                  <button onClick={() => setForm(emptyForm)} className="px-4 py-2 rounded-lg border">Reset</button>
                </>
              )}
            </div>
          </div>

          <div className="mt-6 space-y-3">
            {users.length === 0 && !loading && <div className="text-sm text-gray-500">No delivery staff found.</div>}
            {users.map((u) => {
              const displayName = u.name || u.userName || u.fullName || u.firstName || 'N/A';
              const displayEmail = u.email || u.emailId || u.emailAddress || 'N/A';
              const displayPhone = u.phone || u.phoneNumber || u.mobileNumber || 'N/A';
              return (
                <div key={u.cognitoId || u.id || u.email} className="rounded-xl border border-gray-200 p-4 flex items-center justify-between">
                  <div>
                    <div className="font-medium">{displayName}</div>
                    <div className="text-sm text-gray-500">{displayEmail} • {displayPhone}</div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => navigator.clipboard?.writeText(displayEmail || '')} className="bg-white border border-gray-300 px-3 py-2 rounded-md text-gray-700">Copy Email</button>
                    <button onClick={() => handleEdit(u)} className="bg-yellow-500 px-3 py-2 text-white rounded-md">Edit</button>
                    <button onClick={() => handleDelete(u.cognitoId || u.id)} className="bg-red-600 px-3 py-2 text-white rounded-md">Delete</button>
                  </div>
                </div>
              );
            })}
          </div></div>
        </div>
      </div>
    </div>
  );
};

export default StaffManagement;
